import { Fragment, useState } from 'react';
import type { TenantTrunkStatus } from '@arcnet/contracts';
import { useTrunkStatus } from '../../lib/api/hooks-products.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge, SectionHead,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { TrunkRecord, TrunkHealth, TrunkRegistration } from '../carriers/TrunkLive.js';

/**
 * The trunks this account's calls use, as the CUSTOMER sees them: the carrier,
 * whether it is up, whether we are registered with it, and its record. Never
 * hosts, credentials or the carrier's own replies -- those are staff's.
 */
export function TrunkStatusTab({ tenantId }: { tenantId: string }) {
  const status = useTrunkStatus(tenantId);
  const rows = status.data?.data ?? [];
  const [open, setOpen] = useState<string | null>(null);

  const live = rows.filter((r) => r.is_enabled && r.trunk_status === 'active');
  const down = live.filter((r) => r.health === 'down');
  const failing = live.filter((r) => r.registration === 'failing');

  return (
    <>
      <SectionHead title="Trunks" sub="Carrier connections for this account." />

      {status.error && <ErrorBanner error={status.error} />}
      {/* Problems only. A banner confirming that all is well is one more thing
          to read on every visit, and it trains people to skip banners. */}
      {(down.length > 0 || failing.length > 0) && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }} role="alert">
          <Icon name="warning" size={14} />
          <span>
            {down.length > 0
              ? `${down.map((r) => r.carrier_name).join(', ')} is not answering. Calls over it will fail.`
              : `Not registered with ${failing.map((r) => r.carrier_name).join(', ')}. Incoming calls cannot reach you.`}
          </span>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Carrier</th><th>Used for</th><th>Status</th><th>Registration</th>
                <th className="arc-td-right">Uptime 24 h</th><th />
              </tr>
            </thead>
            {status.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr style={r.is_enabled ? undefined : { opacity: 0.5 }}>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{r.carrier_name}</span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}>{r.trunk_name}</span>
                        </div>
                      </td>
                      <td><Badge tone="muted">{r.direction === 'both' ? 'in and out' : r.direction}</Badge></td>
                      <td>
                        {r.trunk_status === 'active'
                          ? <TrunkHealth status={r.health} latencyMs={r.latency_ms} />
                          : <StatusBadge status={r.trunk_status} />}
                      </td>
                      <td><TrunkRegistration status={registrationWord(r.registration)} /></td>
                      <td className="arc-td-right arc-num">
                        {r.uptime_24h_pct !== null ? `${r.uptime_24h_pct}%` : '—'}
                      </td>
                      <td className="arc-td-right">
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          aria-expanded={open === r.id}
                          onClick={() => setOpen(open === r.id ? null : r.id)}>
                          {open === r.id ? 'Close' : 'Record'}
                        </button>
                      </td>
                    </tr>
                    {open === r.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 12, background: 'var(--arc-surface-2)' }}>
                          <TrunkRecord trunkId={r.trunk_id} tenantId={tenantId} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {status.isSuccess && rows.length === 0 && (
          <Empty icon="carrier" title="No trunk connected"
            hint="Calls between extensions work. Calls to and from outside numbers need a trunk from your provider." />
        )}
      </div>
    </>
  );
}

/** The customer summary's registration, in the same words staff see. */
function registrationWord(r: TenantTrunkStatus['registration']): 'registered' | 'pending' | 'unregistered' | 'unknown' | null {
  switch (r) {
    case 'registered': return 'registered';
    case 'registering':
    case 'not_registered': return 'pending';
    case 'failing': return 'unregistered';
    case 'not_required': return null;
    default: return 'unknown';
  }
}
